import { api } from '@/utils/api'
import type { InstagramAnalyticsResponse, SearchResponse, InstagramAnalyticsInsights, SavedAnalyticsResponse, SaveInfluencerRequest, SavedInfluencersResponse } from '@/types'

export interface AnalyticsRequest {
  username: string
  password: string
  target: string
  days?: number
}

export interface SearchRequest {
  user_query: string
  type?: 'basic' | 'deep'
}

export const instagramService = {
  async analyzeAccount(params: AnalyticsRequest): Promise<InstagramAnalyticsResponse> {
    const searchParams = new URLSearchParams({
      username: params.username,
      password: params.password,
      target: params.target,
      days: (params.days || 7).toString(),
    })
    
    return api.get(`/v0_1/instagram/analytics?${searchParams}`)
  },

  async searchInfluencers(params: SearchRequest): Promise<SearchResponse> {
    const searchParams = new URLSearchParams({
      user_query: params.user_query,
      type: params.type || 'basic',
    })
    
    return api.get(`/v0_1/instagram/search?${searchParams}`)
  },

  async getAccountAnalytics(uuid: string): Promise<InstagramAnalyticsInsights> {
    return api.get(`/v0_1/instagram/account/${uuid}`)
  },

  async getSavedAnalyticsPaged(
    params: { limit?: number; cursor?: string | null; q?: string | null },
    options?: RequestInit
  ): Promise<{
    success: boolean;
    total_batch: number;
    profiles: any[];
    next_cursor?: string | null;
  }> {
    const searchParams = new URLSearchParams()
    if (params.limit) searchParams.set('limit', String(params.limit))
    if (params.cursor) searchParams.set('cursor', params.cursor || '')
    if (params.q) searchParams.set('q', params.q)
    const qs = searchParams.toString()
    try {
      return await api.get(`/v0_1/instagram/account/paged${qs ? `?${qs}` : ''}`, options)
    } catch (e: any) {
      // Fallback for environments where paged endpoint isn't deployed yet
      if (e?.status === 404) {
        // Legacy fallback removed per policy: avoid bulk fetch
        const profiles: any[] = []
        const limited = params.limit ? profiles.slice(0, params.limit) : profiles
        return { success: true, total_batch: limited.length, profiles: limited, next_cursor: null }
      }
      throw e
    }
  },

  async saveInfluencer(data: SaveInfluencerRequest): Promise<{ success: boolean; message: string; save_id: string }> {
    return api.post('/v0_1/instagram/save', data)
  },

  async getSavedInfluencers(): Promise<SavedInfluencersResponse> {
    return api.get('/v0_1/instagram/saved')
  },

  async deleteSavedInfluencer(saveId: string): Promise<{ success: boolean; message: string; save_id: string }> {
    return api.delete(`/v0_1/instagram/saved/${saveId}`)
  },

  async deleteAnalyticsRecord(analyticsId: string): Promise<{ success: boolean; message: string; analytics_id: string }> {
    return api.delete(`/v0_1/instagram/account/${analyticsId}`)
  },

  async getGlobalAccounts(): Promise<SavedAnalyticsResponse> {
    return api.get('/v0_1/instagram/global-accounts')
  },

  async refreshAnalytics(analyticsId: string): Promise<{ success: boolean; message: string; analytics_id: string }> {
    return api.post(`/v0_1/instagram/account/${analyticsId}/refresh`)
  },

  async getAccountSummary(): Promise<{
    success: boolean;
    summary: {
      total_influencers: number;
      total_followers: number;
      average_days_analyzed: number;
      last_added: string;
    };
  }> {
    try {
      return await api.get('/v0_1/instagram/account/summary')
    } catch (e: any) {
      if (e?.status === 404) {
        // Fallback removed: return empty summary when summary endpoint not available
        const profiles: any[] = []
        const byUsername: Record<string, { followers: number; daysSum: number; count: number; lastTs: string }> = {}
        for (const p of profiles) {
          const username = (p.target_username || p.profile_overview?.username || '').trim()
          const followers = p.profile_overview?.followers || 0
          const days = p.days_analyzed || 0
          const ts = p.processing_timestamp || ''
          if (!username) continue
          if (!byUsername[username]) {
            byUsername[username] = { followers, daysSum: days, count: 1, lastTs: ts }
          } else {
            const rec = byUsername[username]
            rec.followers = Math.max(rec.followers, followers)
            rec.daysSum += days
            rec.count += 1
            rec.lastTs = ts > rec.lastTs ? ts : rec.lastTs
          }
        }
        const total_influencers = Object.keys(byUsername).length
        const total_followers = Object.values(byUsername).reduce((s, v) => s + v.followers, 0)
        const average_days_analyzed = total_influencers > 0 ? (Object.values(byUsername).reduce((s, v) => s + v.daysSum, 0) / total_influencers) : 0
        const last_added = Object.values(byUsername).reduce((m, v) => (v.lastTs > m ? v.lastTs : m), '')
        return { success: true, summary: { total_influencers, total_followers, average_days_analyzed, last_added } }
      }
      throw e
    }
  },
} 